import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useParams } from "@tanstack/react-router";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Eye,
  FileText,
  Search,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import { useApi } from "@/api";
import {
  HierarchyBrowser,
  InlineStat,
  Pagination,
} from "@/components/manage/HierarchyBrowser";

export const Route = createFileRoute("/manage/datasets/$id/files/$assetId")({
  component: FileContentPage,
});

interface TableCells {
  headers: string[];
  rows: string[][];
  truncated: boolean;
}

interface ContentUnit {
  id: string;
  content_kind: string | null;
  locator: string | null;
  section_path: string[];
  snippet: string;
  text: string;
  prefix: string | null;
  caption: string | null;
  has_blob: boolean;
  has_image: boolean;
  table: TableCells | null;
}

interface ContentResponse {
  asset_id: string;
  filename: string;
  asset_type: string;
  summary: {
    total_units: number;
    kind_counts: Record<string, number>;
    sub_assets: Record<string, number>;
  };
  units: ContentUnit[];
  matched_total: number;
  offset: number;
  limit: number;
}

// The "spice cabinet" content taxonomy (DESIGN.md): each extracted content kind
// gets a muted pastel spine so type is legible at a glance.
const KIND_META: Record<string, { label: string; spice: string }> = {
  para: { label: "Prose", spice: "#b5c4a9" }, // cardamom
  list: { label: "List", spice: "#b5c4a9" },
  quote: { label: "Quote", spice: "#c3b8d1" }, // lavender
  code: { label: "Code", spice: "#b8c2cc" }, // mist
  table: { label: "Table", spice: "#e4c27a" }, // turmeric
  figure: { label: "Figure", spice: "#d8afa6" }, // rose
  note: { label: "Note", spice: "#c3b8d1" }, // lavender
  heading: { label: "Heading", spice: "#e89e6c" }, // saffron
  section: { label: "Section", spice: "#e89e6c" },
  slide: { label: "Slide", spice: "#e89e6c" },
};

// Asset-aware so a null/uncategorized kind (e.g. a spreadsheet's whole-table
// unit) reads as "Table"/"Content", never "unknown".
function kindMeta(
  k: string | null,
  assetType?: string,
): { label: string; spice: string } {
  if (k && KIND_META[k]) return KIND_META[k];
  if (k && k !== "uncategorized") {
    return { label: k.charAt(0).toUpperCase() + k.slice(1), spice: "#c9bca0" };
  }
  const isSheet = /xlsx|xls|csv|tsv|sheet|ods/i.test(assetType ?? "");
  return { label: isSheet ? "Table" : "Content", spice: "#c9bca0" };
}

const SUBASSET_LABEL: Record<string, string> = {
  table_subset: "Tables",
  figure: "Figures",
  xlsx_sheet: "Sheets",
};

const PAGE = 50;

interface KindGroup {
  kind: string | null;
  key: string;
  label: string;
  spice: string;
  count: number;
}

// The sidebar outline is built from `summary.kind_counts` — a whole-file,
// page-independent signal. (A section_path tree built from the loaded page
// would only navigate ~50 of a multi-thousand-unit file and silently lie.)
// Selecting a kind drives the SERVER `?kind=` filter, which spans pagination.
function buildKindGroups(
  kindCounts: Record<string, number>,
  totalUnits: number,
  assetType: string,
): KindGroup[] {
  const groups = Object.entries(kindCounts)
    .sort((a, b) => b[1] - a[1])
    .map(([rawKind, count]) => {
      const m = kindMeta(rawKind, assetType);
      return {
        kind: rawKind,
        key: rawKind || "_null",
        label: m.label,
        spice: m.spice,
        count,
      };
    });
  return [
    { kind: null, key: "_all", label: "All", spice: "#c9bca0", count: totalUnits },
    ...groups,
  ];
}

function FileContentPage() {
  const { id: datasetId, assetId } = useParams({
    from: "/manage/datasets/$id/files/$assetId",
  });
  const api = useApi();

  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [kind, setKind] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  // Decorative figures (thin bars/rules) are hidden by default — the admin can
  // opt in to inspect them. The agent's MCP tools always hide them.
  const [showDecorative, setShowDecorative] = useState(false);

  // Search spans all kinds: typing clears the kind selection.
  useEffect(() => {
    const t = setTimeout(() => {
      setQDebounced(q);
      setOffset(0);
      if (q) setKind(null);
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: [
      "file-content",
      datasetId,
      assetId,
      kind,
      qDebounced,
      offset,
      showDecorative,
    ],
    queryFn: () => {
      const params = new URLSearchParams({
        offset: String(offset),
        limit: String(PAGE),
      });
      if (kind) params.set("kind", kind);
      if (qDebounced) params.set("q", qDebounced);
      if (showDecorative) params.set("include_decorative", "true");
      return api.request<ContentResponse>(
        `/admin/datasets/${datasetId}/files/${assetId}/content?${params}`,
      );
    },
    // Keep the prior page mounted while a kind switch / page change / search
    // refetches, so the two-pane shell doesn't flash a full skeleton.
    placeholderData: (prev) => prev,
  });

  const assetType = data?.asset_type;
  // Only doc types carry figures — gate the "show decorative" toggle to them.
  const canHaveFigures = /pdf|docx?|pptx?|html?/i.test(assetType ?? "");
  const summary = data?.summary;
  const kindCounts = summary?.kind_counts ?? {};
  const subAssets = summary?.sub_assets ?? {};
  const matched = data?.matched_total ?? 0;

  const kindGroups = useMemo(
    () => buildKindGroups(kindCounts, summary?.total_units ?? 0, assetType ?? ""),
    [kindCounts, summary?.total_units, assetType],
  );

  const toggleGroup = (key: string) =>
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const selectKind = (g: KindGroup) => {
    setKind(g.kind);
    setOffset(0);
    if (g.kind) toggleGroup(g.key);
  };

  // Scroll-to-unit for the lazy sidebar leaves (a secondary orientation aid).
  const unitRefs = useRef<Map<string, HTMLElement>>(new Map());
  const reducedMotion = useMemo(
    () =>
      typeof window !== "undefined" &&
      !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
    [],
  );
  const scrollToUnit = (id: string) =>
    unitRefs.current
      .get(id)
      ?.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "start" });

  const pageIndex = Math.floor(offset / PAGE);
  const lastPage = Math.max(0, Math.ceil(matched / PAGE) - 1);
  const startRow = matched === 0 ? 0 : offset + 1;
  const endRow = Math.min(offset + PAGE, matched);

  if (isLoading && !data) {
    return (
      <div className="mx-auto max-w-[1200px] px-10 py-10">
        <Skeleton className="h-10 w-60" />
        <Skeleton className="mt-8 h-72 w-full" />
      </div>
    );
  }

  if (isError && !data) {
    return (
      <div className="mx-auto max-w-[1200px] px-10 py-10">
        <p className="text-sm text-[var(--color-semantic-error)]">
          Couldn't load extracted content for this file.
        </p>
      </div>
    );
  }

  if (!data) return null;

  const sortedKinds = Object.entries(kindCounts).sort((a, b) => b[1] - a[1]);
  const topKind = sortedKinds[0];
  const subTotal = Object.values(subAssets).reduce((a, b) => a + b, 0);
  const subHint =
    Object.entries(subAssets)
      .map(([k, n]) => `${n} ${SUBASSET_LABEL[k] ?? k}`)
      .join(" · ") || undefined;

  return (
    <HierarchyBrowser
      crumb={
        <Link
          to="/manage/datasets/$id"
          params={{ id: datasetId }}
          className="mb-4 inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-3" /> Back to files
        </Link>
      }
      header={{
        eyebrow: "Dataset · File content",
        tabTitle: [data?.filename ?? "File", "Datasets"],
        title: (
          <span className="inline-flex items-center gap-3">
            <FileText className="size-7 text-muted-foreground" />
            {data?.filename ?? "File"}
          </span>
        ),
        description:
          "Extracted content from this file — pick a content type, read each unit, trace it to its citation.",
      }}
      stats={[
        {
          label: "Extracted units",
          value: (summary?.total_units ?? 0).toLocaleString(),
          hint: assetType,
        },
        {
          label: "Dominant kind",
          value: topKind ? kindMeta(topKind[0], assetType).label : "—",
          hint: topKind ? topKind[1].toLocaleString() : undefined,
        },
        {
          label: "Tables",
          // A spreadsheet's whole-sheet unit is the "uncategorized" (Table)
          // bucket, so count it here too — else every xlsx/csv reads "0 Tables".
          value: (
            (kindCounts.table ?? 0) + (kindCounts.uncategorized ?? 0)
          ).toLocaleString(),
          hint: "extracted",
        },
        {
          label: "Sub-files",
          value: subTotal.toLocaleString(),
          hint: subHint,
        },
      ]}
      toolbar={
        <div className="mb-4 flex shrink-0 items-center gap-3">
          <div className="relative w-full sm:max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search this file's content…"
              className="h-9 w-full rounded-md border border-[var(--color-hairline)] bg-card pl-9 pr-3 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-[var(--color-brand-terracotta)] focus:ring-[3px] focus:ring-[rgba(201,123,74,0.25)]"
            />
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-3">
            {canHaveFigures && (
              <button
                type="button"
                onClick={() => {
                  setShowDecorative((v) => !v);
                  setOffset(0);
                }}
                aria-pressed={showDecorative}
                title="Decorative figures are thin bars/rules the extractor over-captures — hidden by default (and from the agent). Toggle to inspect them."
                className={cn(
                  "flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11px] transition",
                  showDecorative
                    ? "border-transparent bg-[var(--color-card-tint-lavender)] text-[var(--color-brand-purple-800)]"
                    : "border-[var(--color-hairline)] text-muted-foreground hover:text-foreground",
                )}
              >
                <Eye className="size-3.5" />
                {showDecorative ? "Decorative shown" : "Show decorative"}
              </button>
            )}
            <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
              {kind ? kindMeta(kind, assetType).label : "All"} ·{" "}
              {matched.toLocaleString()} matched
            </span>
          </div>
        </div>
      }
      sidebarLabel="Outline"
      sidebar={kindGroups.map((g) => {
        const isActive = kind === g.kind;
        const open = openGroups.has(g.key);
        return (
          <li key={g.key}>
            <button
              type="button"
              onClick={() => selectKind(g)}
              aria-current={isActive ? "true" : undefined}
              aria-expanded={g.kind !== null ? open : undefined}
              className={cn(
                "flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left transition",
                isActive
                  ? "bg-[var(--color-card-tint-lavender)] text-[var(--color-brand-purple-800)]"
                  : "hover:bg-foreground/5",
              )}
            >
              <span
                aria-hidden
                className="h-4 w-[3px] shrink-0 rounded-full"
                style={{ backgroundColor: g.spice }}
              />
              {g.kind !== null &&
                (open ? (
                  <ChevronDown className="size-3 shrink-0 text-muted-foreground" />
                ) : (
                  <ChevronRight className="size-3 shrink-0 text-muted-foreground" />
                ))}
              <span className="flex-1 truncate text-[13px] font-medium">
                {g.label}
              </span>
              <span
                className={cn(
                  "shrink-0 font-mono text-[10px] tabular-nums",
                  isActive
                    ? "text-[var(--color-brand-purple-800)]/70"
                    : "text-muted-foreground",
                )}
              >
                {g.count.toLocaleString()}
              </span>
            </button>
            {/* Lazy leaves: only the expanded, selected group; only the loaded
                page's units — pure scroll-targets, not the count of record. */}
            {g.kind !== null &&
              open &&
              isActive &&
              data?.units.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => scrollToUnit(u.id)}
                  style={{
                    paddingLeft: `${28 + 10 * Math.min(u.section_path?.length ?? 0, 3)}px`,
                  }}
                  className="flex w-full items-center gap-2 rounded-md py-1.5 pr-2.5 text-left transition hover:bg-foreground/5"
                >
                  <span className="min-w-0 flex-1 truncate text-[12px] text-[var(--color-charcoal)]">
                    {u.caption ||
                      u.section_path?.[u.section_path.length - 1] ||
                      u.snippet ||
                      "—"}
                  </span>
                  {u.locator && (
                    <span className="shrink-0 font-mono text-[10px] text-muted-foreground">
                      {u.locator}
                    </span>
                  )}
                </button>
              ))}
          </li>
        );
      })}
    >
      {/* Per-view info strip — twin of the data browser's per-table strip. */}
      <div className="mb-2 flex shrink-0 flex-wrap items-center gap-x-5 gap-y-1.5 rounded-lg border border-[var(--color-hairline)] bg-[var(--color-surface-soft)] px-4 py-2.5 text-xs">
        <InlineStat
          label="Viewing"
          value={
            <span className="inline-flex items-center gap-1.5">
              <span
                aria-hidden
                className="h-2 w-2 rounded-full"
                style={{
                  backgroundColor: kind ? kindMeta(kind, assetType).spice : "#c9bca0",
                }}
              />
              {kind ? kindMeta(kind, assetType).label : "All units"}
            </span>
          }
        />
        <InlineStat
          label="Units"
          value={
            <span className="font-mono tabular-nums">{matched.toLocaleString()}</span>
          }
        />
        <InlineStat
          label="Showing"
          value={
            <span className="font-mono tabular-nums">
              {startRow}–{endRow}
            </span>
          }
        />
      </div>

      {/* Reading column — the document on cream paper. */}
      <div className="relative min-h-0 flex-1 overflow-auto rounded-lg border border-[var(--color-hairline)] bg-card">
        {!data?.units?.length ? (
          <EmptyContentPanel filtered={!!(qDebounced || kind)} />
        ) : (
          <div className="mx-auto max-w-[680px] divide-y divide-[var(--color-hairline-soft)] px-8 py-2">
            {data.units.map((u) => (
              <UnitArticle
                key={u.id}
                unit={u}
                assetType={assetType}
                datasetId={datasetId}
                assetId={assetId}
                register={(el) => {
                  if (el) unitRefs.current.set(u.id, el);
                  else unitRefs.current.delete(u.id);
                }}
              />
            ))}
          </div>
        )}
      </div>

      {!!matched && (
        <Pagination
          pageIndex={pageIndex}
          lastPage={lastPage}
          pageSize={PAGE}
          onPageSize={() => {}}
          onPrev={() => setOffset((o) => Math.max(0, o - PAGE))}
          onNext={() => setOffset((o) => o + PAGE)}
          onFirst={() => setOffset(0)}
          onLast={() => setOffset(lastPage * PAGE)}
          totalRows={matched}
          startRow={startRow}
          endRow={endRow}
          fetching={isFetching}
          hidePageSize
        />
      )}
    </HierarchyBrowser>
  );
}

function UnitArticle({
  unit,
  assetType,
  datasetId,
  assetId,
  register,
}: {
  unit: ContentUnit;
  assetType?: string;
  datasetId: string;
  assetId: string;
  register: (el: HTMLElement | null) => void;
}) {
  const m = kindMeta(unit.content_kind, assetType);
  const figureSrc =
    unit.has_image && unit.locator
      ? `/api/admin/datasets/${datasetId}/files/${assetId}/figure?locator=${encodeURIComponent(unit.locator)}`
      : null;
  return (
    <article ref={register} className="group relative py-6">
      <span
        aria-hidden
        className="absolute -left-4 top-6 bottom-6 w-[3px] rounded-full"
        style={{ backgroundColor: m.spice }}
      />
      <div className="mb-1.5 flex items-center gap-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
          {m.label}
        </span>
        {unit.locator && (
          <button
            type="button"
            onClick={() => navigator.clipboard?.writeText(unit.locator ?? "")}
            title="Copy citation"
            aria-label={`Copy citation ${unit.locator}`}
            className="font-mono text-[11px] text-[var(--color-brand-terracotta)] hover:underline"
          >
            {unit.locator}
          </button>
        )}
      </div>
      {unit.section_path?.length > 0 && (
        <p
          className="mb-1.5 truncate text-[11px] text-muted-foreground"
          title={unit.section_path.join(" › ")}
        >
          {unit.section_path.join(" › ")}
        </p>
      )}
      {unit.caption && (
        <p className="mb-1.5 text-sm font-medium text-[var(--color-charcoal)]">{unit.caption}</p>
      )}
      <UnitBody
        kind={unit.content_kind}
        text={unit.text || unit.snippet}
        hasBlob={unit.has_blob}
        table={unit.table}
        figureSrc={figureSrc}
      />
      {unit.prefix && (
        <details className="mt-3 border-t border-[var(--color-hairline-soft)] pt-2.5">
          <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
            Retrieval context
          </summary>
          <p className="mt-1.5 text-xs leading-relaxed text-[var(--color-slate)]">{unit.prefix}</p>
        </details>
      )}
    </article>
  );
}

// Per-kind typeset rendering — this is where the reader reads as a document.
function UnitBody({
  kind,
  text,
  hasBlob,
  table,
  figureSrc,
}: {
  kind: string | null;
  text: string;
  hasBlob: boolean;
  table?: TableCells | null;
  figureSrc?: string | null;
}) {
  const body = text || "(no text)";
  switch (kind) {
    case "heading":
    case "section":
    case "slide":
      return (
        <h3
          className="text-lg leading-snug text-[var(--color-charcoal)]"
          style={{ fontFamily: "var(--font-display)" }}
        >
          {body}
        </h3>
      );
    case "quote":
      return (
        <p className="whitespace-pre-wrap border-l-2 border-[var(--color-hairline-strong)] pl-3 text-[15px] italic leading-relaxed text-[var(--color-slate)]">
          {body}
        </p>
      );
    case "code":
      return (
        <pre className="overflow-auto rounded-md border border-[var(--color-hairline)] bg-[var(--color-surface-soft)] p-3 font-mono text-xs leading-relaxed">
          {body}
        </pre>
      );
    case "table":
    case "uncategorized":
    case "":
    case null:
      // Render the actual grid when we have structured cells; fall back to the
      // raw text only when a table unit somehow lacks them.
      return table && table.rows.length ? (
        <UnitTable table={table} />
      ) : (
        <div className="overflow-auto rounded-md border border-[var(--color-hairline)] bg-[var(--color-surface-soft)] p-3">
          <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed">
            {body}
          </pre>
          {hasBlob && (
            <p className="mt-2 font-mono text-[10px] text-muted-foreground">
              Table extracted — view in Browse data
            </p>
          )}
        </div>
      );
    case "figure":
      return (
        <div>
          {figureSrc ? (
            <img
              src={figureSrc}
              alt={body !== "(no text)" ? body : "Extracted figure"}
              loading="lazy"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
              className="max-h-[360px] w-auto rounded-md border border-[var(--color-hairline)] bg-card object-contain"
            />
          ) : hasBlob ? (
            <span className="inline-block rounded-[var(--radius-sm)] bg-[var(--color-card-tint-rose)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--color-charcoal)]">
              [image]
            </span>
          ) : null}
          {body !== "(no text)" && (
            <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-[var(--color-slate)]">
              {body}
            </p>
          )}
        </div>
      );
    default:
      return (
        <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-foreground">
          {body}
        </p>
      );
  }
}

// A table unit's structured cells rendered as an actual grid (the same shape as
// the data browser's table), scrollable inside the reading column.
function UnitTable({ table }: { table: TableCells }) {
  const cols = Math.max(
    table.headers.length,
    ...table.rows.map((r) => r.length),
    1,
  );
  const idx = Array.from({ length: cols });
  return (
    <div>
      <div className="max-h-[420px] overflow-auto rounded-md border border-[var(--color-hairline)] bg-card">
        <table
          className="min-w-full text-xs"
          style={{ borderCollapse: "separate", borderSpacing: 0 }}
        >
          {table.headers.length > 0 && (
            <thead>
              <tr>
                {idx.map((_, i) => (
                  <th
                    key={i}
                    className="sticky top-0 z-10 whitespace-nowrap border-b border-[var(--color-hairline)] bg-[var(--color-surface-soft)] px-2.5 py-1.5 text-left font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground"
                  >
                    {table.headers[i] ?? ""}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {table.rows.map((row, ri) => (
              <tr
                key={ri}
                className="border-b border-[var(--color-hairline-soft)] last:border-b-0"
              >
                {idx.map((_, ci) => (
                  <td
                    key={ci}
                    className="max-w-[260px] truncate whitespace-nowrap px-2.5 py-1.5 font-mono text-[11px]"
                    title={row[ci] ?? ""}
                  >
                    {row[ci] ?? ""}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {table.truncated && (
        <p className="mt-1.5 font-mono text-[10px] text-muted-foreground">
          Showing first {table.rows.length} rows — full data in Browse data.
        </p>
      )}
    </div>
  );
}

function EmptyContentPanel({ filtered }: { filtered: boolean }) {
  if (filtered) {
    return (
      <p className="px-8 py-16 text-center text-sm text-muted-foreground">
        No content matches this filter.
      </p>
    );
  }
  return (
    <div className="px-8 py-16 text-center">
      <FileText className="mx-auto size-6 text-muted-foreground" />
      <h2
        className="mt-3 text-base font-semibold tracking-tight"
        style={{ fontFamily: "var(--font-display)" }}
      >
        No extracted content yet
      </h2>
      <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
        The file may still be processing.
      </p>
    </div>
  );
}
